<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="formData.type"
          :items="typeList"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn loại phiếu']"
        >
          <template v-slot:label>
            Loại phiếu giao dịch <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="8">
        <v-text-field
          v-model="formData.title"
          placeholder="VD: Nhập lô 10 máy chiếu mới, Xuất 50 bút bi cho khối 1..."
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng nhập tiêu đề']"
        >
          <template v-slot:label>
            Tiêu đề phiếu <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field
          v-model="formData.transactionDate"
          type="date"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn ngày']"
        >
          <template v-slot:label>
            Ngày thực hiện <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>

      <!-- Vị trí xuất/nhập phụ thuộc vào loại phiếu -->
      <v-col cols="12" md="4" v-if="formData.type === 'EXPORT' || formData.type === 'TRANSFER'">
        <v-select
          v-model="formData.fromLocation"
          :items="locations"
          item-text="name"
          item-value="id"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn vị trí xuất']"
        >
          <template v-slot:label>
            Từ vị trí / Phòng xuất <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="4" v-if="formData.type === 'IMPORT' || formData.type === 'TRANSFER'">
        <v-select
          v-model="formData.toLocation"
          :items="locations"
          item-text="name"
          item-value="id"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn vị trí đích']"
        >
          <template v-slot:label>
            Đến vị trí / Phòng đích <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field
          v-model="formData.performer"
          label="Người lập phiếu"
          placeholder="Họ tên người lập"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="formData.deliverer"
          label="Người giao hàng / bàn giao"
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="formData.receiver"
          label="Người nhận"
          outlined
          dense
        />
      </v-col>

      <!-- Bảng danh sách thiết bị trong phiếu -->
      <v-col cols="12">
        <v-card outlined class="pa-3 grey lighten-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-subtitle-2 font-weight-bold primary--text text-uppercase">
              Danh sách thiết bị ({{ formData.items.length }})
            </span>
            <v-btn small color="primary" depressed @click="addItem">
              <v-icon small left>mdi-plus</v-icon> Thêm thiết bị
            </v-btn>
          </div>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 40%">Thiết bị <span class="red--text font-weight-bold">*</span></th>
                  <th style="width: 20%">Số lượng <span class="red--text font-weight-bold">*</span></th>
                  <th style="width: 20%">Đơn vị</th>
                  <th style="width: 20%">Đơn giá (VNĐ)</th>
                  <th style="width: 50px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="formData.items.length === 0">
                  <td colspan="5" class="text-center grey--text py-3">
                    Chưa có thiết bị nào. Nhấn "Thêm thiết bị" để bắt đầu.
                  </td>
                </tr>
                <tr v-for="(item, index) in formData.items" :key="index">
                  <td class="py-2">
                    <v-select
                      v-model="item.asset"
                      :items="assetList"
                      item-text="name"
                      item-value="id"
                      placeholder="Chọn thiết bị"
                      outlined
                      dense
                      hide-details
                      @change="onAssetSelected(item)"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      min="1"
                      outlined
                      dense
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model="item.unit"
                      outlined
                      dense
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="item.price"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details
                    />
                  </td>
                  <td class="text-center">
                    <v-btn icon small color="error" @click="removeItem(index)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-textarea
          v-model="formData.note"
          label="Ghi chú thêm"
          outlined
          dense
          rows="2"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { FacilityLocation, Asset } from '@/plugins/api'

export default {
  name: 'TransactionForm',
  data() {
    return {
      valid: true,
      locations: [],
      assetList: [],
      typeList: [
        { text: 'Nhập kho', value: 'IMPORT' },
        { text: 'Xuất kho', value: 'EXPORT' },
        { text: 'Luân chuyển vị trí', value: 'TRANSFER' },
        { text: 'Thanh lý', value: 'LIQUIDATION' }
      ],
      formData: {
        type: 'IMPORT',
        title: '',
        transactionDate: new Date().toISOString().substring(0, 10),
        fromLocation: null,
        toLocation: null,
        performer: '',
        deliverer: '',
        receiver: '',
        note: '',
        items: []
      }
    }
  },
  async created() {
    try {
      const [locs, assets] = await Promise.all([
        FacilityLocation.search({ _limit: -1, isDelete: false }),
        Asset.search({ _limit: -1, isDelete: false })
      ])
      this.locations = locs || []
      this.assetList = assets || []
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    addItem() {
      this.formData.items.push({
        asset: null,
        assetName: '',
        quantity: 1,
        unit: 'Chiếc',
        price: 0
      })
    },
    removeItem(index) {
      this.formData.items.splice(index, 1)
    },
    onAssetSelected(item) {
      const found = this.assetList.find(a => a.id === item.asset)
      if (found) {
        item.assetName = found.name
        item.unit = found.unit || 'Chiếc'
        item.price = found.purchasePrice || 0
      }
    },
    validate() {
      if (this.formData.items.length === 0) {
        this.$alert.error('Vui lòng thêm ít nhất 1 thiết bị vào phiếu')
        return false
      }
      for (const it of this.formData.items) {
        if (!it.asset) {
          this.$alert.error('Vui lòng chọn thiết bị cho từng dòng')
          return false
        }
        if (!it.quantity || it.quantity <= 0) {
          this.$alert.error('Số lượng thiết bị phải lớn hơn 0')
          return false
        }
      }
      return this.$refs.form.validate()
    },
    getData() {
      let totalAmount = 0
      this.formData.items.forEach(it => {
        totalAmount += (it.quantity || 0) * (it.price || 0)
      })
      return {
        ...this.formData,
        totalAmount
      }
    },
    resetValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    }
  }
}
</script>
